import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Bed, Presentation, ForkKnife, Buildings, ArrowRight } from "@phosphor-icons/react";

const KIND_META = {
  rooms: { label: "Rooms", Icon: Bed, color: "#002FA7", bg: "#E0E7FF" },
  meeting: { label: "Meeting", Icon: Presentation, color: "#B45309", bg: "#FEF3C7" },
  fnb: { label: "F&B", Icon: ForkKnife, color: "#047857", bg: "#D1FAE5" },
};

function Stat({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] p-4">
      <div className="overline text-[#4B5563]">{label}</div>
      <div className="font-display font-black text-2xl text-[#111827] mt-1">{value}</div>
      {sub && <div className="text-xs text-[#4B5563] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SpaceTracker() {
  const [kind, setKind] = useState("all");
  const [entryType, setEntryType] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["space-entries", kind, entryType],
    queryFn: () => api.spaceEntries({ kind, entry_type: entryType }),
  });

  const d = data || { entries: [], totals: {}, agreement_totals: {}, actual_totals: {}, by_property: [] };

  return (
    <div className="space-y-6" data-testid="space-tracker">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total entries" value={d.totals.count || 0} />
        <Stat label="Room-nights" value={d.totals.room_nights || 0} />
        <Stat label="Agreement value" value={money(d.agreement_totals.revenue || 0)} sub={`${d.agreement_totals.count || 0} contracted`} />
        <Stat label="Actual booked" value={money(d.actual_totals.revenue || 0)} sub={`${d.actual_totals.count || 0} booked`} />
      </div>

      {(d.by_property || []).length > 0 && (
        <div>
          <span className="overline text-[#4B5563] block mb-2">By property</span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.by_property.map((p) => (
              <div key={p.hotel_id || "unassigned"} className="border border-[#E5E7EB] p-3" data-testid={`space-prop-${p.hotel_id || "unassigned"}`}>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-[#111827]">
                  <Buildings size={14} weight="bold" color="#002FA7" /> {p.hotel_name || "Unassigned"}
                </div>
                <div className="text-sm text-[#4B5563] mt-1">{p.room_nights} room-nights · {p.count} entries</div>
                <div className="font-display font-black text-lg text-[#111827]">{money(p.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <select data-testid="space-filter-kind" value={kind} onChange={(e) => setKind(e.target.value)}
          className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">All types</option>
          <option value="rooms">Guest rooms</option>
          <option value="meeting">Meeting space</option>
          <option value="fnb">Food & beverage</option>
        </select>
        <select data-testid="space-filter-type" value={entryType} onChange={(e) => setEntryType(e.target.value)}
          className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">Agreement & actual</option>
          <option value="agreement">Agreement (contracted)</option>
          <option value="actual">Actual (booked)</option>
        </select>
      </div>

      <div className="border border-[#E5E7EB]">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 bg-[#F3F4F6] overline text-[#4B5563]">
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Property</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-4">Details</div>
          <div className="col-span-1 text-right">Value</div>
        </div>
        {isLoading && <div className="px-4 py-6 text-sm text-[#4B5563]">Loading…</div>}
        {!isLoading && d.entries.length === 0 && (
          <div className="px-4 py-8 text-sm text-[#4B5563]" data-testid="space-tracker-empty">No entries yet. Add rooms, meeting space or F&B from any lead.</div>
        )}
        {d.entries.map((e) => {
          const meta = KIND_META[e.kind] || KIND_META.rooms;
          const Icon = meta.Icon;
          return (
            <Link key={e.id} to={`/leads/${e.lead_id}`} data-testid={`space-row-${e.id}`}
              className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-t border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors items-center">
              <div className="md:col-span-3 font-semibold text-[#111827] text-sm flex items-center gap-1">{e.company_name} <ArrowRight size={12} className="text-[#9CA3AF]" /></div>
              <div className="md:col-span-2 text-sm text-[#4B5563]">{e.hotel_name || "—"}</div>
              <div className="md:col-span-2 flex items-center gap-1.5">
                <span className="inline-flex items-center justify-center w-6 h-6" style={{ background: meta.bg }}><Icon size={13} weight="bold" color={meta.color} /></span>
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 ${e.entry_type === "agreement" ? "bg-[#E0E7FF] text-[#002FA7]" : "bg-[#D1FAE5] text-[#047857]"}`}>{e.entry_type}</span>
              </div>
              <div className="md:col-span-4 text-sm text-[#4B5563]">
                {e.kind === "rooms" && `${e.num_rooms || 0} × ${e.room_type || "rooms"} · ${money(e.nightly_rate)}/nt · ${e.nights || 0} nights`}
                {e.kind === "meeting" && `${e.space_name || "Space"} · ${e.day_part || ""} · ${money(e.space_rate)}`}
                {e.kind === "fnb" && `F&B min ${money(e.fnb_minimum)}`}
              </div>
              <div className="md:col-span-1 text-right font-display font-black text-[#111827]">{money(e.revenue)}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
